import React, { useEffect, useState } from "react";
import Navbar from "../../components/Navbar";
import {
  User,
  Lock,
  Building2,
  Camera,
  Save,
  X,
  ChevronRight,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Link as LinkIcon,
  MapPin,
  FileText,
  AlertCircle,
  Shield,
  Eye,
  EyeOff,
  LoaderCircle,
  Navigation,
  CheckCircle,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import GuestRequests from "../../requests/GuestRequests";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import ProfileRequests from "../../requests/vendor/ProfileRequests";
import { setUser } from "../../redux/UserSlice";
import { logout, testResetActions } from "../../redux/store";
import { useNavigate } from "react-router-dom";

const ProfileSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user?.user) || null;
  const isMainVendor = useSelector((state) => state?.user?.isMainVendor) || false;
  const [activeTab, setActiveTab] = useState("brand");
  const [profileImage, setProfileImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: "Too Weak",
    width: "0%",
    color: "bg-gray-200",
  });
  const [categories, setCategories] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [other, setOther] = useState(() => {
    if (!user?.brand_category || !categories) return false;
    return !categories.includes(user.brand_category);
  });

  const [locations] = useState([
    "Accra",
    "Kumasi",
    "Tamale",
    "Cape Coast",
    "Sekondi-Takoradi",
    "Sunyani",
    "Koforidua",
    "Ho",
    "Wa",
    "Bolgatanga",
    "Other",
  ]);

  // Initialize customLocation if user's location is not in the list
  const [customLocation, setCustomLocation] = useState(() => {
    if (user?.location && !locations.slice(0, -1).includes(user.location)) {
      return user.location;
    }
    return "";
  });

  const [payload, setPayload] = useState({
    brand_name: user?.brand_name || "",
    brand_category: user?.brand_category || "",
    email: user?.email || "",
    phone_number: user?.phone_number || "",
    facebook: user?.facebook || "",
    instagram: user?.instagram || "",
    twitter: user?.twitter || "",
    website: user?.website || "",
    location: user?.location || "",
    google_map_url: user?.google_map_url || "",
  });

  const [passwordPayload, setPasswordPayload] = useState({
    old_password: "",
    password: "",
    password_confirmation: "",
  });

  // Set initial location to "Other" if user's location is not in the list
  useEffect(() => {
    if (user?.location && !locations.slice(0, -1).includes(user.location)) {
      setCustomLocation(user.location);
    }
  }, [user?.location]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true);
        const category_api_response = await GuestRequests.getCategories();
        if (category_api_response.data.status_code === "000") {
          const categoryNames = category_api_response?.data?.data.map(
            (category) => category.name
          );
          setCategories(categoryNames);
          if (user?.brand_category) {
            setOther(!categoryNames.includes(user.brand_category));
          }
          setIsLoading(false);
        } else {
          toast.error(
            category_api_response.data.reason ||
              "Sorry, Categories could not be fetched"
          );
        }
      } catch (error) {
        toast.error("Sorry, Categories could not be fetched");
      } finally {
        setIsLoading(false);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    // Check if passwords match whenever either password changes
    if (passwordPayload.password_confirmation || passwordPayload.password) {
      setPasswordsMatch(
        passwordPayload.password === passwordPayload.password_confirmation
      );
    }
  }, [passwordPayload.password, passwordPayload.password_confirmation]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(URL.createObjectURL(file));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setProfileImage(URL.createObjectURL(file));
    }
  };

  const checkPasswordStrength = (pass) => {
    let score = 0;
    const checks = {
      length: pass.length >= 8,
      hasUpperCase: /[A-Z]/.test(pass),
      hasLowerCase: /[a-z]/.test(pass),
      hasNumber: /\d/.test(pass),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(pass),
    };

    // Increment score for each requirement met
    Object.values(checks).forEach((check) => {
      if (check) score += 1;
    });

    // Map scores to strength levels
    const strengthMap = {
      0: { label: "Too Weak", width: "0%", color: "bg-gray-200" },
      1: { label: "Weak", width: "25%", color: "bg-red-500" },
      2: { label: "Fair", width: "50%", color: "bg-yellow-500" },
      3: { label: "Good", width: "75%", color: "bg-blue-500" },
      4: { label: "Strong", width: "100%", color: "bg-green-500" },
      5: { label: "Strong", width: "100%", color: "bg-green-500" },
    };

    setPasswordStrength({
      score,
      ...strengthMap[score],
    });

    return checks;
  };

  const isValidGoogleMapsUrl = (url) => {
    if (!url) return false;
    const googleMapsPatterns = [
      /^https?:\/\/maps\.google\.com\//,
      /^https?:\/\/www\.google\.com\/maps\//,  
      /^https?:\/\/goo\.gl\/maps\//,
      /^https?:\/\/maps\.app\.goo\.gl\//,
      /^https?:\/\/maps\.app\.goo\.gl/,
      /^https?:\/\/goo\.gl/,
    ];
    return googleMapsPatterns.some(pattern => pattern.test(url));
  };

  const handleBrandInfoSubmit = async () => {
    console.log("Form submission started");
    console.log("Payload:", payload);
    try {
      setIsSubmitting(true);
      const updateResponse = await ProfileRequests.updateProfileData(payload);
      console.log("Update Response:", updateResponse);

      if (updateResponse?.data?.status_code === "200") {
        const newUserData = updateResponse.data.data;
        const {
          brand_name,
          brand_category,
          email,
          phone_number,
          website,
          facebook,
          instagram,
          twitter,
          location,
          google_map_url,
        } = newUserData;
        dispatch(
          setUser({
            ...user,
            brand_name,
            brand_category,
            email,
            phone_number,
            facebook: facebook,
            twitter: twitter,
            instagram: instagram,
            location,
            website,
            google_map_url,
          })
        );
        toast.success(
          updateResponse.data.reason || "Great, Brand Info Successfully Updated"
        );
      } else {
        toast.error(
          updateResponse.data.reason ||
            "Sorry, The brand info could not be updated"
        );
      }
    } catch (error) {
      console.log("Brand Update Error: ", error);
      toast.error("Sorry brand info could not be updated");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    setTimeout(() => {
      navigate("/signin");
    }, 100);
  };

  const handleTestReset = () => {
    testResetActions();
  };

  const handlePasswordUpdate = async () => {
    try {
      setPasswordSaving(true);
      const password_response = await ProfileRequests.changeProfilePassword(
        passwordPayload
      );
      if (password_response?.data?.status_code === "200") {
        toast.warning("Great, Login again with your new password.");
        setPasswordSaving(false);
        handleLogout();
      } else {
        toast.error(
          password_response?.data?.reason ||
            "Sorry, You could not reset your password. Try Again!!"
        );
        setPasswordSaving(false);
      }
    } catch (e) {
      console.log("PASSWORD_CHANGE_API_REQUEST_ERROR: ", e);
      toast.error("Sorry, Your password could not be updated");
    } finally {
      setPasswordSaving(false);
    }
  };

  const cancelUpdateProcess = () => {
    setPayload({
      brand_name: user?.brand_name || "",
      brand_category: user?.brand_category || "",
      email: user?.email || "",
      phone_number: user?.phone_number || "",
      facebook: user?.facebook || "",
      instagram: user?.instagram || "",
      twitter: user?.twitter || "",
      website: user?.website || "",
      location: user?.location || "",
      google_map_url: user?.google_map_url || "",
    });
    if (user?.location && !locations.slice(0, -1).includes(user.location)) {
      setCustomLocation(user.location);
    } else {
      setCustomLocation("");
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar page="Profile Settings" />
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="overflow-hidden bg-white rounded-2xl shadow-xl">
          {/* Header */}
          <div className="relative px-4 py-6 border-b border-gray-200 bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] sm:px-8 sm:py-8">
            <h1 className="text-2xl font-bold text-white sm:text-3xl">
              Profile Settings
            </h1>
            <p className="mt-2 text-sm text-white/80">
              Manage your account settings and preferences
            </p>
            <div className="absolute top-0 right-0 w-24 h-24 rounded-bl-full sm:w-32 sm:h-32 bg-white/10"></div>
          </div>

          {/* Content */}
          <div className="flex flex-col lg:flex-row">
            {/* Sidebar - Mobile Dropdown */}
            <div className="px-4 py-4 lg:hidden">
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-gray-200 focus:border-[#FF6C6C] focus:ring focus:ring-[#FF6C6C]/20"
              >
                {[
                  { icon: Building2, name: "Brand Info", id: "brand" },
                  { icon: Lock, name: "Security", id: "security" },
                ].map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Sidebar - Desktop */}
            <div className="hidden px-4 py-6 w-72 border-r border-gray-200 lg:block">
              <nav className="space-y-2">
                {[
                  { icon: Building2, name: "Brand Info", id: "brand" },
                  { icon: Lock, name: "Security", id: "security" },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`flex items-center justify-between w-full px-4 py-3 text-sm rounded-xl transition-all duration-200 ${
                      activeTab === item.id
                        ? "bg-[#FF6C6C] text-white shadow-lg shadow-[#FF6C6C]/30"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center">
                      <item.icon className="mr-3 w-5 h-5" />
                      {item.name}
                    </div>
                    <ChevronRight
                      className={`w-4 h-4 transition-transform ${
                        activeTab === item.id ? "rotate-90" : ""
                      }`}
                    />
                  </button>
                ))}
              </nav>
            </div>

            {/* Main Content */}
            <div className="flex-1 px-4 py-6 sm:px-8">
              {activeTab === "security" && (
                <div className="space-y-6 sm:space-y-8">
                  {/* Current Status */}
                  <div className="p-4 bg-gray-50 rounded-2xl sm:p-6">
                    <h2 className="text-xl font-semibold text-gray-900">
                      Security Settings
                    </h2>
                    {/* Temporary test button */}
                    {/* <button
                      onClick={handleTestReset}
                      className="px-4 py-2 mt-4 text-white bg-red-500 rounded-lg hover:bg-red-600"
                    >
                      Test Reset Actions
                    </button> */}
                  </div>

                  {/* Password Update Form */}
                  <div className="p-4 bg-white rounded-2xl border border-gray-200 sm:p-6">
                    <h3 className="text-lg font-medium text-gray-900">
                      Update Password
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Ensure your account stays secure by using a strong
                      password
                    </p>

                    <form
                      className="mt-6 space-y-6"
                      onSubmit={(e) => {
                        e.preventDefault();
                        handlePasswordUpdate();
                      }}
                    >
                      <div className="space-y-5">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700">
                            Current Password
                          </label>
                          <div className="relative">
                            <input
                              type={showCurrentPassword ? "text" : "password"}
                              className="block w-full px-4 py-3 pr-10 rounded-xl border-gray-200 focus:border-[#FF6C6C] focus:ring focus:ring-[#FF6C6C]/20 transition-shadow"
                              placeholder="Enter your current password"
                              value={passwordPayload.old_password}
                              onChange={(e) =>
                                setPasswordPayload({
                                  ...passwordPayload,
                                  old_password: e.target.value,
                                })
                              }
                            />
                            <button
                              type="button"
                              className="flex absolute inset-y-0 right-0 items-center px-3 text-gray-400 hover:text-gray-600"
                              onClick={() =>
                                setShowCurrentPassword(!showCurrentPassword)
                              }
                            >
                              {showCurrentPassword ? (
                                <EyeOff className="w-5 h-5" />
                              ) : (
                                <Eye className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700">
                            New Password
                          </label>
                          <div className="relative">
                            <input
                              type={showNewPassword ? "text" : "password"}
                              className="block w-full px-4 py-3 pr-10 rounded-xl border-gray-200 focus:border-[#FF6C6C] focus:ring focus:ring-[#FF6C6C]/20 transition-shadow"
                              placeholder="Enter your new password"
                              value={passwordPayload.password}
                              onChange={(e) => {
                                setPasswordPayload({
                                  ...passwordPayload,
                                  password: e.target.value,
                                });
                                checkPasswordStrength(e.target.value);
                              }}
                            />
                            <button
                              type="button"
                              className="flex absolute inset-y-0 right-0 items-center px-3 text-gray-400 hover:text-gray-600"
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                            >
                              {showNewPassword ? (
                                <EyeOff className="w-5 h-5" />
                              ) : (
                                <Eye className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                          <div className="mt-2">
                            <div className="flex space-x-4">
                              <div className="overflow-hidden flex-1 h-1 bg-gray-200 rounded-full">
                                <div
                                  className={`h-full ${passwordStrength.color} transition-all duration-300`}
                                  style={{ width: passwordStrength.width }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-500">
                                {passwordStrength.label}
                              </span>
                            </div>
                            <ul className="mt-2 space-y-1 text-sm text-gray-500">
                              {[
                                {
                                  check: password.length >= 8,
                                  text: "At least 8 characters",
                                },
                                {
                                  check:
                                    /[A-Z]/.test(password) &&
                                    /[a-z]/.test(password),
                                  text: "Mix of uppercase & lowercase letters",
                                },
                                {
                                  check: /\d/.test(password),
                                  text: "At least one number",
                                },
                                {
                                  check: /[!@#$%^&*(),.?":{}|<>]/.test(
                                    password
                                  ),
                                  text: "At least one special character",
                                },
                              ].map((requirement, index) => (
                                <li key={index} className="flex items-center">
                                  <span
                                    className={`mr-2 w-4 h-4 ${
                                      requirement.check
                                        ? "text-green-500"
                                        : "text-gray-300"
                                    }`}
                                  >
                                    {requirement.check ? "✓" : "○"}
                                  </span>
                                  {requirement.text}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700">
                            Confirm New Password
                          </label>
                          <div className="relative">
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              className={`block w-full px-4 py-3 pr-10 rounded-xl border-gray-200 focus:border-[#FF6C6C] focus:ring focus:ring-[#FF6C6C]/20 transition-shadow ${
                                !passwordsMatch &&
                                passwordPayload.password_confirmation
                                  ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                                  : ""
                              }`}
                              placeholder="Confirm your new password"
                              value={passwordPayload.password_confirmation}
                              onChange={(e) => {
                                setPasswordPayload({
                                  ...passwordPayload,
                                  password_confirmation: e.target.value,
                                });
                                setConfirmPassword(e.target.value);
                              }}
                            />
                            <button
                              type="button"
                              className="flex absolute inset-y-0 right-0 items-center px-3 text-gray-400 hover:text-gray-600"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                            >
                              {showConfirmPassword ? (
                                <EyeOff className="w-5 h-5" />
                              ) : (
                                <Eye className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                          {!passwordsMatch && confirmPassword && (
                            <p className="mt-1 text-sm text-red-500">
                              Passwords do not match
                            </p>
                          )}
                          {passwordsMatch && confirmPassword && (
                            <p className="mt-1 text-sm text-green-500">
                              Passwords match
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex flex-col space-y-3 sm:flex-row sm:justify-end sm:space-y-0 sm:space-x-4">
                          <button
                            type="submit"
                            className="px-6 py-3 flex items-center justify-center gap-1 text-sm font-medium text-white bg-[#FF6C6C] rounded-xl border border-transparent shadow-lg shadow-[#FF6C6C]/30 hover:bg-[#FF5555] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF6C6C] transition-all duration-200"
                            disabled={
                              !passwordsMatch ||
                              !passwordPayload.password ||
                              !passwordPayload.old_password ||
                              !passwordPayload.password_confirmation
                            }
                          >
                            {passwordSaving ? (
                              <LoaderCircle className="mr-2 w-4 h-4 animate-spin" />
                            ) : (
                              <Lock className="mr-2 w-4 h-4" />
                            )}
                            {passwordSaving ? "Saving..." : "Update Password"}
                          </button>
                          <button
                            type="button"
                            className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              )}
              {activeTab === "brand" && (
                <div className="space-y-6 sm:space-y-8">
                  {/* Brand Status Card */}
                  <div className="p-6 bg-gray-50 rounded-2xl">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {isMainVendor ? "Company Information" : "Your Information"}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                      {isMainVendor ? "Add your company details and social media presence" : "Add your details and social media presence"}
                    </p>
                  </div>

                  {/* Brand Details Form */}
                  <form
                    onSubmit={(e) => {
                      console.log("Form onSubmit triggered");
                      e.preventDefault();
                      handleBrandInfoSubmit();
                    }}
                  >
                    {/* Company Details Form - Redesigned */}
                    <div className="overflow-hidden bg-gradient-to-br from-white to-white rounded-3xl border border-gray-100 shadow-2xl via-gray-50/20">
                      {/* Header Section */}
                      <div className="relative px-8 py-8 bg-gradient-to-r to-gray-50 border-b border-gray-100 from-slate-50">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#FF6C6C] to-[#FF8F8F] rounded-2xl shadow-lg">
                            <Building2 className="w-8 h-8 text-white" />
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold text-gray-900">
                              {isMainVendor ? "Company Details" : "Your Details"}
                            </h3>
                            <p className="mt-1 text-gray-600">
                              {isMainVendor ? "Essential information about your business" : "Essential information about you"}
                            </p>
                          </div>
                        </div>
                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#FF6C6C]/5 to-[#FF8F8F]/10 rounded-bl-full"></div>
                      </div>

                      {/* Form Content */}
                      <div className="p-8">
                        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                          {/* Company Name */}
                          <div className="relative group">
                            <div className="p-6 bg-gradient-to-br rounded-2xl border transition-all duration-300 from-blue-50/50 to-indigo-50/30 border-blue-100/50 hover:border-blue-200/70 hover:shadow-lg">
                              <div className="flex items-center mb-4">
                                <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-md">
                                  <Building2 className="w-5 h-5 text-white" />
                                </div>
                                <div className="ml-3">
                                  <label className="text-sm font-semibold text-gray-900">
                                    {isMainVendor ? "Company Name" : "Your Name"}
                                  </label>
                                  <p className="text-xs text-gray-500">
                                    {isMainVendor ? "Your business name" : "Your full name"}
                                  </p>
                                </div>
                              </div>
                              <div className="relative">
                                <input
                                  type="text"
                                  className="block w-full px-4 py-4 bg-white/80 backdrop-blur-sm rounded-xl border-2 border-gray-200 focus:border-[#FF6C6C] focus:ring-4 focus:ring-[#FF6C6C]/10 transition-all duration-200 text-gray-700 placeholder-gray-400 hover:bg-white group-hover:border-blue-300"
                                  placeholder="Enter company name"
                                  value={payload.brand_name}
                                  onChange={(e) =>
                                    setPayload({
                                      ...payload,
                                      brand_name: e.target.value,
                                    })
                                  }
                                />
                                {payload.brand_name && (
                                  <div className="absolute top-4 right-4">
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Email */}
                          <div className="relative group">
                            <div className="p-6 bg-gradient-to-br rounded-2xl border from-gray-50/80 to-gray-100/40 border-gray-200/60">
                              <div className="flex items-center mb-4">
                                <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-500 rounded-xl shadow-md">
                                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                                  </svg>
                                </div>
                                <div className="ml-3">
                                  <label className="flex items-center text-sm font-semibold text-gray-900">
                                    Email Address
                                    <span className="px-2 py-0.5 ml-2 text-xs text-gray-600 bg-gray-200 rounded-full">Read-only</span>
                                  </label>
                                  <p className="text-xs text-gray-500">Your registered email</p>
                                </div>
                              </div>
                              <div className="relative">
                                <input
                                  readOnly
                                  value={payload.email}
                                  type="email"
                                  className="block px-4 py-4 w-full text-gray-600 rounded-xl border-2 border-gray-200 backdrop-blur-sm cursor-not-allowed bg-gray-100/60"
                                  placeholder="your@email.com"
                                />
                                <div className="absolute top-4 right-4">
                                  <Shield className="w-5 h-5 text-gray-400" />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Phone Number */}
                          <div className="relative group">
                            <div className="p-6 bg-gradient-to-br rounded-2xl border from-gray-50/80 to-gray-100/40 border-gray-200/60">
                              <div className="flex items-center mb-4">
                                <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-500 rounded-xl shadow-md">
                                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                  </svg>
                                </div>
                                <div className="ml-3">
                                  <label className="flex items-center text-sm font-semibold text-gray-900">
                                    Phone Number
                                    <span className="px-2 py-0.5 ml-2 text-xs text-gray-600 bg-gray-200 rounded-full">Read-only</span>
                                  </label>
                                  <p className="text-xs text-gray-500">Your contact number</p>
                                </div>
                              </div>
                              <div className="relative">
                                <input
                                  readOnly
                                  value={payload.phone_number}
                                  type="tel"
                                  className="block px-4 py-4 w-full text-gray-600 rounded-xl border-2 border-gray-200 backdrop-blur-sm cursor-not-allowed bg-gray-100/60"
                                  placeholder="+ (233) 000-000-000"
                                />
                                <div className="absolute top-4 right-4">
                                  <Shield className="w-5 h-5 text-gray-400" />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Industry - Only show for main vendors */}
                          {isMainVendor && (
                            <div className="relative group">
                              <div className="p-6 bg-gradient-to-br rounded-2xl border transition-all duration-300 from-purple-50/50 to-pink-50/30 border-purple-100/50 hover:border-purple-200/70 hover:shadow-lg">
                                <div className="flex items-center mb-4">
                                  <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-md">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                  </div>
                                  <div className="ml-3">
                                    <label className="text-sm font-semibold text-gray-900">
                                      Industry Category
                                    </label>
                                    <p className="text-xs text-gray-500">Your business sector</p>
                                  </div>
                                </div>
                                <div className="space-y-3">
                                  <select
                                    name="brand_category"
                                    className="block px-4 py-4 w-full bg-white/80 backdrop-blur-sm rounded-xl border-2 border-gray-200 focus:border-[#FF6C6C] focus:ring-4 focus:ring-[#FF6C6C]/10 transition-all duration-200 text-gray-700 hover:bg-white group-hover:border-purple-300"
                                    value={other ? "Other" : payload.brand_category}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (value === "Other") {
                                        setOther(true);
                                      } else {
                                        setOther(false);
                                        setPayload((prev) => ({
                                          ...prev,
                                          brand_category: value,
                                        }));
                                      }
                                    }}
                                  >
                                    <option value="">Select Industry</option>
                                    {categories &&
                                      [...categories, "Other"].map((category) => (
                                        <option key={category} value={category}>
                                          {category}
                                        </option>
                                      ))}
                                  </select>
                                  {other && (
                                    <div className="relative">
                                      <input
                                        type="text"
                                        className="block w-full px-4 py-4 bg-white/80 backdrop-blur-sm rounded-xl border-2 border-gray-200 focus:border-[#FF6C6C] focus:ring-4 focus:ring-[#FF6C6C]/10 transition-all duration-200 text-gray-700 placeholder-gray-400"
                                        placeholder="Enter Custom Category"
                                        value={payload.brand_category}
                                        onChange={(e) =>
                                          setPayload((prev) => ({
                                            ...prev,
                                            brand_category: e.target.value,
                                          }))
                                        }
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Location - Only show for main vendors */}
                          {isMainVendor && (
                            <div className="relative group">
                              <div className="p-6 bg-gradient-to-br rounded-2xl border transition-all duration-300 from-green-50/50 to-emerald-50/30 border-green-100/50 hover:border-green-200/70 hover:shadow-lg">
                                <div className="flex items-center mb-4">
                                  <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl shadow-md">
                                    <MapPin className="w-5 h-5 text-white" />
                                  </div>
                                  <div className="ml-3">
                                    <label className="text-sm font-semibold text-gray-900">
                                      Business Location
                                    </label>
                                    <p className="text-xs text-gray-500">Where you operate from</p>
                                  </div>
                                </div>
                                <div className="space-y-3">
                                  <select
                                    name="location"
                                    className="block px-4 py-4 w-full bg-white/80 backdrop-blur-sm rounded-xl border-2 border-gray-200 focus:border-[#FF6C6C] focus:ring-4 focus:ring-[#FF6C6C]/10 transition-all duration-200 text-gray-700 hover:bg-white group-hover:border-green-300"
                                    value={
                                      !locations.slice(0, -1).includes(payload.location)
                                        ? "Other"
                                        : payload.location
                                    }
                                    onChange={(e) => {
                                      const selectedLocation = e.target.value;
                                      if (selectedLocation === "Other") {
                                        const existingCustomLocation = customLocation || user?.location;
                                        setCustomLocation(existingCustomLocation);
                                        setPayload((prev) => ({
                                          ...prev,
                                          location: existingCustomLocation,
                                        }));
                                      } else {
                                        setPayload((prev) => ({
                                          ...prev,
                                          location: selectedLocation,
                                        }));
                                        setCustomLocation("");
                                      }
                                    }}
                                  >
                                    <option value="">Select Location</option>
                                    {locations.map((location) => (
                                      <option key={location} value={location}>
                                        {location}
                                      </option>
                                    ))}
                                  </select>
                                  {(!locations.slice(0, -1).includes(payload.location) ||
                                    payload.location === "Other") && (
                                    <div className="relative">
                                      <input
                                        type="text"
                                        className="block w-full px-4 py-4 bg-white/80 backdrop-blur-sm rounded-xl border-2 border-gray-200 focus:border-[#FF6C6C] focus:ring-4 focus:ring-[#FF6C6C]/10 transition-all duration-200 text-gray-700 placeholder-gray-400"
                                        placeholder="Enter your location"
                                        value={customLocation}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          const stringOnlyRegex = /^[a-zA-Z\s\-'\.]*$/;
                                          
                                          if (stringOnlyRegex.test(value)) {
                                            setCustomLocation(value);
                                            setPayload((prev) => ({
                                              ...prev,
                                              location: value || prev.location,
                                            }));
                                          }
                                        }}
                                        onKeyPress={(e) => {
                                          const char = String.fromCharCode(e.which);
                                          if (/[0-9]/.test(char)) {
                                            e.preventDefault();
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Google Maps Location - Full Width Enhanced */}
                        {isMainVendor && (
                          <div className="mt-8">
                            <div className="p-8 bg-gradient-to-br rounded-2xl border-2 transition-all duration-300 from-red-50/50 via-orange-50/30 to-yellow-50/20 border-red-100/60 hover:border-red-200/80 hover:shadow-xl group">
                              <div className="mb-6 text-center">
                                <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl shadow-lg">
                                  <svg 
                                    className="w-8 h-8 text-white" 
                                    viewBox="0 0 24 24" 
                                    fill="none"
                                  >
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
                                  </svg>
                                </div>
                                <h4 className="mb-2 text-xl font-bold text-gray-900">
                                  Google Maps Location (Optional)
                                </h4>
                                <p className="flex justify-center items-center text-gray-600">
                                  Help customers find your business easily
                                </p>
                              </div>

                              <div className="relative mb-6">
                                <div className="flex absolute inset-y-0 left-0 items-center pl-6 pointer-events-none">
                                  <svg 
                                    className="h-6 w-6 text-[#4285F4]" 
                                    viewBox="0 0 24 24" 
                                    fill="none"
                                  >
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#EA4335"/>
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#4285F4"/>
                                    <circle cx="12" cy="9" r="2.5" fill="#EA4335"/>
                                    <path d="M12 7c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2z" fill="white"/>
                                  </svg>
                                </div>
                                <input
                                  type="url"
                                  
                                  className={`block w-full pl-16 pr-16 py-5 bg-white/90 backdrop-blur-sm rounded-2xl border-3 transition-all duration-200 text-lg ${
                                    !payload.google_map_url
                                      ? "border-gray-300 focus:border-[#FF6C6C] focus:ring-6 focus:ring-[#FF6C6C]/15 group-hover:border-gray-400"
                                      : isValidGoogleMapsUrl(payload.google_map_url)
                                      ? "border-green-300 focus:border-green-500 focus:ring-6 focus:ring-green-500/15 group-hover:border-green-400"
                                      : "border-red-300 focus:border-red-500 focus:ring-6 focus:ring-red-500/15 group-hover:border-red-400"
                                  } text-gray-700 placeholder-gray-400 shadow-inner`}
                                  placeholder="https://maps.google.com/... (optional)"
                                  value={payload.google_map_url}
                                  onChange={(e) =>
                                    setPayload({
                                      ...payload,
                                      google_map_url: e.target.value,
                                    })
                                  }
                                />
                                <div className="flex absolute inset-y-0 right-0 items-center pr-6">
                                  {payload.google_map_url && isValidGoogleMapsUrl(payload.google_map_url) ? (
                                    <div className="flex items-center space-x-2">
                                      <CheckCircle className="w-6 h-6 text-green-500" />
                                      <span className="text-sm font-medium text-green-600">Valid</span>
                                    </div>
                                  ) : payload.google_map_url ? (
                                    <div className="flex items-center space-x-2">
                                      <AlertCircle className="w-6 h-6 text-red-500" />
                                      <span className="text-sm font-medium text-red-600">Invalid</span>
                                    </div>
                                  ) : null}
                                </div>
                              </div>

                              <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200/50">
                                <h5 className="flex items-center mb-4 text-base font-bold text-blue-900">
                                  <Navigation className="mr-3 w-5 h-5" />
                                  Quick Setup Guide
                                </h5>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                  <ol className="space-y-2 text-sm list-decimal list-inside text-blue-800">
                                    <li>Visit <span className="px-2 py-1 font-mono bg-white rounded-md shadow-sm">maps.google.com</span></li>
                                    <li>Search for your business name</li>
                                    <li>Click on your business listing</li>
                                    <li>Click "Share" and copy the link</li>
                                  </ol>
                                  <div className="p-4 rounded-xl border border-blue-100 bg-white/60">
                                    <p className="mb-2 text-xs font-medium text-blue-700">📍 Pro Tip:</p>
                                    <p className="text-xs text-blue-600">
                                      Make sure your Google Business Profile is verified for better visibility!
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {payload.google_map_url && !isValidGoogleMapsUrl(payload.google_map_url) && (
                                <div className="flex items-center p-4 mt-4 space-x-3 bg-red-50 rounded-xl border border-red-200">
                                  <AlertCircle className="flex-shrink-0 w-5 h-5 text-red-500" />
                                  <p className="text-sm font-medium text-red-700">
                                    Please enter a valid Google Maps URL (e.g., https://maps.google.com/...)
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Social Media & Website Links */}
                    {isMainVendor && (
                      <div className="p-8 mt-6 bg-gradient-to-br from-white to-white rounded-3xl border border-gray-100 shadow-lg via-gray-50/30">
                        <div className="mb-8 text-center">
                          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#FF6C6C] to-[#FF8F8F] rounded-2xl mb-4 shadow-lg">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                          </div>
                          <h3 className="mb-2 text-2xl font-bold text-gray-900">
                            Social Media & Website
                          </h3>
                          <p className="mx-auto max-w-md text-gray-600">
                            Connect your social media accounts and website to boost your brand visibility and reach more customers
                          </p>
                        </div>

                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          {/* Facebook */}
                          <div className="relative group">
                            <div className="relative bg-gradient-to-br from-[#1877F2]/5 to-[#1877F2]/10 rounded-2xl p-6 border border-[#1877F2]/20 hover:border-[#1877F2]/40 transition-all duration-300 hover:shadow-lg hover:shadow-[#1877F2]/10">
                              <div className="flex items-center mb-4">
                                <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#1877F2] to-[#1877F2]/80 rounded-xl shadow-lg">
                                  <Facebook className="w-6 h-6 text-white" />
                                </div>
                                <div className="ml-4">
                                  <h4 className="text-lg font-semibold text-gray-900">Facebook</h4>
                                  <p className="text-sm text-gray-500">Share your Facebook page</p>
                                </div>
                              </div>
                              <div className="relative">
                                <input
                                  type="url"
                                  className="block w-full px-4 py-4 bg-white/80 backdrop-blur-sm rounded-xl border-2 border-gray-200 focus:border-[#FF6C6C] focus:ring-4 focus:ring-[#FF6C6C]/10 transition-all duration-200 text-gray-700 placeholder-gray-400 hover:bg-white group-hover:border-[#1877F2]/30"
                                  placeholder="https://facebook.com/your-page"
                                  value={payload.facebook}
                                  onChange={(e) =>
                                    setPayload({
                                      ...payload,
                                      facebook: e.target.value,
                                    })
                                  }
                                />
                                {payload.facebook && (
                                  <div className="absolute top-4 right-4">
                                    <div className="flex justify-center items-center w-6 h-6 bg-green-100 rounded-full">
                                      <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Instagram */}
                          <div className="relative group">
                            <div className="relative bg-gradient-to-br from-[#E4405F]/5 via-[#F56040]/5 to-[#FCAF45]/5 rounded-2xl p-6 border border-[#E4405F]/20 hover:border-[#E4405F]/40 transition-all duration-300 hover:shadow-lg hover:shadow-[#E4405F]/10">
                              <div className="flex items-center mb-4">
                                <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#E4405F] via-[#F56040] to-[#FCAF45] rounded-xl shadow-lg">
                                  <Instagram className="w-6 h-6 text-white" />
                                </div>
                                <div className="ml-4">
                                  <h4 className="text-lg font-semibold text-gray-900">Instagram</h4>
                                  <p className="text-sm text-gray-500">Connect your Instagram</p>
                                </div>
                              </div>
                              <div className="relative">
                                <input
                                  type="url"
                                  className="block w-full px-4 py-4 bg-white/80 backdrop-blur-sm rounded-xl border-2 border-gray-200 focus:border-[#FF6C6C] focus:ring-4 focus:ring-[#FF6C6C]/10 transition-all duration-200 text-gray-700 placeholder-gray-400 hover:bg-white group-hover:border-[#E4405F]/30"
                                  placeholder="https://instagram.com/your-profile"
                                  value={payload.instagram}
                                  onChange={(e) =>
                                    setPayload({
                                      ...payload,
                                      instagram: e.target.value,
                                    })
                                  }
                                />
                                {payload.instagram && (
                                  <div className="absolute top-4 right-4">
                                    <div className="flex justify-center items-center w-6 h-6 bg-green-100 rounded-full">
                                      <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Twitter */}
                          <div className="relative group">
                            <div className="relative bg-gradient-to-br from-[#1DA1F2]/5 to-[#1DA1F2]/10 rounded-2xl p-6 border border-[#1DA1F2]/20 hover:border-[#1DA1F2]/40 transition-all duration-300 hover:shadow-lg hover:shadow-[#1DA1F2]/10">
                              <div className="flex items-center mb-4">
                                <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#1DA1F2] to-[#1DA1F2]/80 rounded-xl shadow-lg">
                                  <Twitter className="w-6 h-6 text-white" />
                                </div>
                                <div className="ml-4">
                                  <h4 className="text-lg font-semibold text-gray-900">Twitter</h4>
                                  <p className="text-sm text-gray-500">Link your Twitter account</p>
                                </div>
                              </div>
                              <div className="relative">
                                <input
                                  type="url"
                                  className="block w-full px-4 py-4 bg-white/80 backdrop-blur-sm rounded-xl border-2 border-gray-200 focus:border-[#FF6C6C] focus:ring-4 focus:ring-[#FF6C6C]/10 transition-all duration-200 text-gray-700 placeholder-gray-400 hover:bg-white group-hover:border-[#1DA1F2]/30"
                                  placeholder="https://twitter.com/your-handle"
                                  value={payload.twitter}
                                  onChange={(e) =>
                                    setPayload({
                                      ...payload,
                                      twitter: e.target.value,
                                    })
                                  }
                                />
                                {payload.twitter && (
                                  <div className="absolute top-4 right-4">
                                    <div className="flex justify-center items-center w-6 h-6 bg-green-100 rounded-full">
                                      <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Website */}
                          <div className="relative group">
                            <div className="relative p-6 bg-gradient-to-br rounded-2xl border transition-all duration-300 from-gray-500/5 to-gray-600/10 border-gray-300/30 hover:border-gray-400/50 hover:shadow-lg hover:shadow-gray-400/10">
                              <div className="flex items-center mb-4">
                                <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl shadow-lg">
                                  <LinkIcon className="w-6 h-6 text-white" />
                                </div>
                                <div className="ml-4">
                                  <h4 className="text-lg font-semibold text-gray-900">Website</h4>
                                  <p className="text-sm text-gray-500">Add your business website</p>
                                </div>
                              </div>
                              <div className="relative">
                                <input
                                  type="url"
                                  className="block w-full px-4 py-4 bg-white/80 backdrop-blur-sm rounded-xl border-2 border-gray-200 focus:border-[#FF6C6C] focus:ring-4 focus:ring-[#FF6C6C]/10 transition-all duration-200 text-gray-700 placeholder-gray-400 hover:bg-white group-hover:border-gray-400/30"
                                  placeholder="https://your-website.com"
                                  value={payload.website}
                                  onChange={(e) =>
                                    setPayload({
                                      ...payload,
                                      website: e.target.value,
                                    })
                                  }
                                />
                                {payload.website && (
                                  <div className="absolute top-4 right-4">
                                    <div className="flex justify-center items-center w-6 h-6 bg-green-100 rounded-full">
                                      <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Social Media Tips */}
                        <div className="p-6 mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
                          <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                              <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-xl">
                                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                            </div>
                            <div className="flex-1">
                              <h4 className="mb-2 text-sm font-semibold text-blue-900">Pro Tips for Social Media</h4>
                              <ul className="space-y-1 text-sm text-blue-700">
                                <li>• Use your business profile URLs for better brand visibility</li>
                                <li>• Keep your social media profiles updated with latest content</li>
                                <li>• Ensure your brand messaging is consistent across all platforms</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Form Actions - Visible for all users */}
                    <div className="flex flex-col mt-8 space-y-3 sm:flex-row sm:justify-end sm:space-y-0 sm:space-x-4">
                      <button
                        type="button"
                        className="px-8 py-4 text-sm font-medium text-gray-700 bg-white rounded-xl border-2 border-gray-200 shadow-sm transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-gray-200/50"
                        onClick={() => {
                          cancelUpdateProcess();
                        }}
                      >
                        Cancel Changes
                      </button>
                      <button
                        type="submit"
                        className="px-8 py-4 text-sm font-semibold text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] rounded-xl border border-transparent shadow-xl shadow-[#FF6C6C]/25 hover:shadow-2xl hover:shadow-[#FF6C6C]/30 hover:from-[#FF5555] hover:to-[#FF7777] focus:outline-none focus:ring-4 focus:ring-[#FF6C6C]/50 transition-all duration-300 transform hover:-translate-y-0.5"
                        disabled={isSubmitting}
                        onClick={(e) => {
                          console.log("Submit button clicked");
                          console.log("Button disabled:", isSubmitting);
                          console.log("isSubmitting:", isSubmitting);
                          console.log("google_map_url:", payload.google_map_url);
                        }}
                      >
                        <div className="flex justify-center items-center">
                          {isSubmitting ? (
                            <svg className="mr-3 -ml-1 w-5 h-5 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <Save className="mr-2 w-5 h-5" />
                          )}
                          {isSubmitting ? "Saving Changes..." : "Save All Changes"}
                        </div>
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
